// src/components/roles/RoleStaffManager.jsx - Translated version
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  Avatar,
  Button,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  TextField,
  IconButton,
  Tooltip,
  Chip,
} from '@mui/material';
import {
  People as PeopleIcon,
  Person as PersonIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  Email as EmailIcon,
} from '@mui/icons-material';
import LocalizedDateDistance from '../../../i18n/LocalizedDateDistance';
import { useStaff } from '../../staff/hooks/useStaff';
import { staffAPI } from '../../staff/services/staff';
import { useStaffPermissions } from '../../auth/hooks/usePermissions';
import { useSnackbar } from '../../../hooks/mui/useSnackBar';
import { useConfirmDialog } from '../../../hooks/mui/useConfirmDialog';
import ProfilePicture from '../../../components/common/ProfilePicture';

export default function RoleStaffManager({ role, onUpdate }) {
  const { t } = useTranslation(['roles', 'common']);
  const { showSuccess, showError } = useSnackbar();
  const { showConfirmDialog, ConfirmDialog } = useConfirmDialog();
  const [staffWithRole, setStaffWithRole] = useState([]);
  const [allStaff, setAllStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [selectedStaffToAssign, setSelectedStaffToAssign] = useState(null);
  const [assigning, setAssigning] = useState(false);

  const {
    assignRole,
    removeRole,
  } = useStaff();
  const { canAssignRoles, canRemoveRoles } = useStaffPermissions();

  // Load data when component mounts or role changes
  useEffect(() => {
    const loadData = async () => {
      if (!role) return;
      
      try {
        setLoading(true);
        
        // All department staff with their roles in one request
        const staffResult = await staffAPI.getDepartmentStaffWithRoles({ include_inactive: false });
        const allStaffMembers = staffResult.data?.staff_members || [];
        setAllStaff(allStaffMembers);
        setStaffWithRole(allStaffMembers.filter(
          (staffMember) => (staffMember.roles || []).some(r => r.role_id === role.role_id),
        ));
      } catch (error) {
        console.error('Error loading staff data:', error);
        showError(t('roles:errors.staff_load_failed'));
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [role, showError, t]);

  const handleAssignRole = useCallback(async () => {
    if (!selectedStaffToAssign || !role) return;

    setAssigning(true);
    try {
      const result = await assignRole(selectedStaffToAssign.staff_member_id, role.role_id);
      if (result.success) {
        // Add to staffWithRole list
        setStaffWithRole(prev => [...prev, selectedStaffToAssign]);
        
        showSuccess(t('roles:success.role_assigned', { 
          roleName: role.name, 
          staffName: selectedStaffToAssign.name 
        }));
        setAssignDialogOpen(false);
        setSelectedStaffToAssign(null);
        onUpdate?.();
      } else {
        showError(result.error || t('roles:errors.assign_failed'));
      }
    } catch (error) {
      console.error('Error assigning role:', error);
      showError(t('roles:errors.assign_failed'));
    } finally {
      setAssigning(false);
    }
  }, [selectedStaffToAssign, role, assignRole, showSuccess, showError, onUpdate, t]);

  const handleRemoveRole = useCallback((staffMember) => {
    console.log('handleRemoveRole called', { staffMember, role });
    
    showConfirmDialog({
      title: t('roles:confirm.remove_role_title'),
      message: t('roles:confirm.remove_role_message', { 
        roleName: role.name, 
        staffName: staffMember.name 
      }),
      confirmText: t('roles:confirm.remove_confirm'),
      cancelText: t('common:buttons.cancel'),
      severity: 'warning',
      onConfirm: async () => {
        console.log('Confirm clicked - removing role');
        try {
          console.log('Removing role:', {
            staffMemberId: staffMember.staff_member_id,
            roleId: role.role_id,
            staffName: staffMember.name,
            roleName: role.name
          });
          
          const result = await removeRole(staffMember.staff_member_id, role.role_id);
          
          console.log('Remove role result:', result);
          
          if (result.success) {
            // Remove from staffWithRole list
            setStaffWithRole(prev => 
              prev.filter(s => s.staff_member_id !== staffMember.staff_member_id)
            );
            
            showSuccess(t('roles:success.role_removed', { 
              roleName: role.name, 
              staffName: staffMember.name 
            }));
            onUpdate?.();
          } else {
            console.error('Remove role failed:', result.error);
            showError(result.error || t('roles:errors.remove_failed'));
          }
        } catch (error) {
          console.error('Error removing role:', error);
          showError(t('roles:errors.remove_failed') + ': ' + (error.message || t('roles:errors.unexpected_error')));
        }
      },
      onCancel: () => {
        console.log('Cancel clicked');
      }
    });
  }, [role, removeRole, showConfirmDialog, showSuccess, showError, onUpdate, t]);

  // Get staff members that don't already have this role
  const availableStaff = allStaff.filter(staff => 
    !staffWithRole.some(assigned => assigned.staff_member_id === staff.staff_member_id)
  );

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (!role) {
    return (
      <Alert severity="info">
        {t('roles:messages.select_role_for_staff')}
      </Alert>
    );
  }

  return (
    <>
      <Card>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
            <Box display="flex" alignItems="center" gap={2}>
              <PeopleIcon color="primary" />
              <Box>
                <Typography variant="h6">
                  {t('roles:staff_manager.title', { roleName: role.name })}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('roles:staff_manager.assigned_count', { 
                    count: staffWithRole.length,
                    defaultValue_plural: t('roles:staff_manager.assigned_count_plural', { count: staffWithRole.length })
                  })}
                </Typography>
              </Box>
            </Box>

            {canAssignRoles && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setAssignDialogOpen(true)}
                disabled={availableStaff.length === 0}
              >
                {t('roles:assign_staff')}
              </Button>
            )}
          </Box>

          {staffWithRole.length === 0 ? (
            <Alert severity="info">
              {t('roles:messages.no_staff_assigned')}
              {canAssignRoles && availableStaff.length > 0 && (
                <Box mt={1}>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => setAssignDialogOpen(true)}
                  >
                    {t('roles:messages.assign_first_staff')}
                  </Button>
                </Box>
              )}
            </Alert>
          ) : (
            <List>
              {staffWithRole.map((staffMember) => (
                <ListItem key={staffMember.staff_member_id} divider>
                  <ListItemIcon>
                    <ProfilePicture
                      profilePictureFileId={staffMember?.profile_picture_file_id}
                      name={staffMember?.name}
                      size={40}
                      color={staffMember?.color}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="body1" fontWeight="medium">
                          {staffMember.name}
                        </Typography>
                        {!staffMember.is_active && (
                          <Chip 
                            label={t('roles:status.inactive')} 
                            size="small" 
                            color="warning" 
                            variant="outlined" 
                          />
                        )}
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          {t("common:labels.id")} {staffMember.staff_member_id.substring(0, 8)}...
                        </Typography>
                        {staffMember.last_login && (
                          <Typography variant="caption" color="text.secondary">
                            {t('roles:staff_manager.last_login', { 
                              time: <LocalizedDateDistance date={staffMember.last_login} />
                            })}
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                  {canRemoveRoles && (
                    <ListItemSecondaryAction>
                      <Tooltip title={t('roles:staff_manager.remove_role_tooltip')}>
                        <IconButton
                          edge="end"
                          onClick={() => handleRemoveRole(staffMember)}
                          color="error"
                        >
                          <RemoveIcon />
                        </IconButton>
                      </Tooltip>
                    </ListItemSecondaryAction>
                  )}
                </ListItem>
              ))}
            </List>
          )}

          {availableStaff.length === 0 && staffWithRole.length > 0 && (
            <Alert severity="info" sx={{ mt: 2 }}>
              {t('roles:messages.all_staff_assigned')}
            </Alert>
          )}

          {allStaff.length === 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              {t('roles:messages.no_staff_in_department')}
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Assign Staff Dialog */}
      <Dialog
        open={assignDialogOpen}
        onClose={() => setAssignDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {t('roles:staff_manager.assign_dialog_title', { roleName: role.name })}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" paragraph>
            {t('roles:staff_manager.assign_dialog_description', { roleName: role.name })}
          </Typography>

          <Autocomplete
            options={availableStaff}
            getOptionLabel={(option) => `${option.name} (ID: ${option.staff_member_id.substring(0, 8)}...)`}
            value={selectedStaffToAssign}
            onChange={(event, value) => setSelectedStaffToAssign(value)}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('roles:staff_manager.select_staff_member')}
                placeholder={t('roles:placeholders.search_staff')}
                margin="normal"
                fullWidth
              />
            )}
            renderOption={({ key, ...props }, option) => (
              <Box key={key} component="li" {...props}>
                <Avatar sx={{ mr: 2, width: 32, height: 32 }}>
                  <PersonIcon fontSize="small" />
                </Avatar>
                <Box>
                  <Typography variant="body2">
                    {option.name}
                    {!option.is_active && (
                      <Chip 
                        label={t('roles:status.inactive')} 
                        size="small" 
                        color="warning" 
                        sx={{ ml: 1 }} 
                      />
                    )}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t("common:labels.id")} {option.staff_member_id.substring(0, 8)}...
                  </Typography>
                </Box>
              </Box>
            )}
            noOptionsText={
              availableStaff.length === 0 
                ? t('roles:messages.no_available_staff')
                : t('roles:messages.no_matching_staff')
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignDialogOpen(false)}>
            {t('common:buttons.cancel')}
          </Button>
          <Button
            onClick={handleAssignRole}
            variant="contained"
            disabled={!selectedStaffToAssign || assigning}
            startIcon={assigning ? <CircularProgress size={16} /> : <AddIcon />}
          >
            {assigning ? t('roles:actions.assigning') : t('roles:actions.assign_role')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Dialog */}
      <ConfirmDialog />
    </>
  );
}